import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { db } from './firebase';
import { collection, onSnapshot, query, where, orderBy, limit } from 'firebase/firestore';
import { ShoppingCart, TrendingUp, RotateCcw, Users, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { BUSINESS_TYPES, getBusinessType } from './businessTypes';
import { codFeeFor } from './dueUtil';

const formatTk = (num) => new Intl.NumberFormat('en-IN').format(Math.round(num || 0));
const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const toDate = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts.seconds) return new Date(ts.seconds * 1000);
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
};

const ChangeBadge = ({ pct }) => {
  if (pct === null) return null;
  const up = pct >= 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '12px', fontWeight: 'bold', color: up ? '#10b981' : '#ef4444' }}>
      {up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
      {Math.abs(pct)}% from yesterday
    </span>
  );
};

const Home = () => {
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [returns, setReturns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [topStats, setTopStats] = useState([]);
  const [businessTypeFilter, setBusinessTypeFilter] = useState('All');

  useEffect(() => {
    // ড্যাশবোর্ডের সব হিসাব (আজ/গতকাল/৭ দিনের ট্রেন্ড/রিসেন্ট অ্যাক্টিভিটি) গত ৩০ দিনের
    // মধ্যেই সীমাবদ্ধ, তাই পুরো sales/returns history না টেনে শুধু গত ৩০ দিনের ডেটা আনা হচ্ছে —
    // এতে সময়ের সাথে sales collection যত বড়ই হোক, প্রতিবার পেজ খোলার read খরচ বাড়বে না।
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);

    const u1 = onSnapshot(query(collection(db, "sales"), where("createdAt", ">=", cutoff)), snap => setSales(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u2 = onSnapshot(collection(db, "products"), snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u3 = onSnapshot(query(collection(db, "returns"), where("createdAt", ">=", cutoff)), snap => setReturns(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u4 = onSnapshot(collection(db, "customers"), snap => setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    // productStats: প্রতিটা প্রোডাক্টের সর্বকালের বিক্রির যোগফল, Sales.js-এর transaction-এ আপডেট হয় —
    // তাই এখানে পুরো sales history না পড়েই সর্বকালের সেরা বিক্রি বের করা যায়। Top 50 আনা হচ্ছে
    // (top 5 নয়) যাতে Business Type দিয়ে ফিল্টার করার পরও প্রতিটা লাইনের জন্য ৫টা আইটেম বের করা যায়।
    const u5 = onSnapshot(query(collection(db, "productStats"), orderBy("qtySold", "desc"), limit(50)), snap => setTopStats(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => { u1(); u2(); u3(); u4(); u5(); };
  }, []);

  const todayKey = dateKey(new Date());
  const yesterdayDate = new Date(); yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterdayKey = dateKey(yesterdayDate);

  // Business Type ফিল্টার করা থাকলে শুধু মিলে যাওয়া আইটেমগুলোর যোগফল দিয়ে হিসাব হয়
  // (ডিসকাউন্ট প্রতিটা লাইনে ভাগ করে হিসাব করা হয় না — এটা একটা আনুমানিক সংখ্যা)।
  // ডেলিভারি চার্জ কখনোই "বিক্রি" না, কিন্তু কাস্টমার-পেইড আর শপ-পেইড আলাদাভাবে হিসাব হয়:
  // কাস্টমার দিলে সেটা totalBill-এর ভেতরেই থাকে, তাই রেভিনিউ থেকে বাদ (এটা পাস-থ্রু, বিক্রি না)।
  // শপ দিলে সেটা totalBill-এ থাকেই না (কাস্টমারকে চার্জ করা হয়নি), কিন্তু দোকানের আসল খরচ —
  // তাই সেটা প্রফিট থেকে বাদ যায়, রেভিনিউ থেকে না (নাহলে ডাবল বাদ পড়ে যেত)।
  const enrichedSales = useMemo(() => sales.map(s => {
    const d = toDate(s.createdAt);
    const items = s.items || [];
    const matchingItems = businessTypeFilter === 'All' ? items : items.filter(it => getBusinessType(it) === businessTypeFilter);
    // শপ-পেইড ডেলিভারি খরচটা তখনই প্রাসঙ্গিক যখন এই সেলটা বর্তমান ফিল্টারের সাথে মেলে
    // (নাহলে অন্য Business Type-এর ডেলিভারি খরচ এই ফিল্টারের প্রফিট থেকে বাদ যেত)
    const relevantToFilter = businessTypeFilter === 'All' || matchingItems.length > 0;
    const shopPaidDelivery = relevantToFilter && s.deliveryPaidBy === 'shop' ? Number(s.deliveryCharge || 0) : 0;
    // Pathao COD টাকার ১% আলাদা কেটে নেয় (ডেলিভারি চার্জের বাইরে, সবসময় দোকানের খরচ) -- প্রফিট থেকে বাদ
    const codFee = relevantToFilter ? codFeeFor(s) : 0;
    // costPrice রেকর্ড করা না থাকলে সেটাকে 0 ধরা হয় (পুরো বিক্রির দামটাই তখন প্রফিট),
    // আগে costPrice না থাকলে সেই আইটেমটার প্রফিট পুরোপুরি বাদ পড়ে যেত (0 দেখাতো)
    const profit = matchingItems.reduce((sum, it) => {
      const cost = Number(it.costPrice) || 0;
      return sum + (it.price - cost) * it.qty;
    }, 0) - shopPaidDelivery - codFee;
    const revenue = businessTypeFilter === 'All'
      ? Number(s.totalBill || 0) - (s.deliveryPaidBy !== 'shop' ? Number(s.deliveryCharge || 0) : 0)
      : matchingItems.reduce((sum, it) => sum + (Number(it.finalItemTotal ?? (it.price * it.qty)) || 0), 0);
    return { ...s, _date: d, _key: d ? dateKey(d) : null, _profit: profit, _revenue: revenue, _matchCount: matchingItems.length };
  }), [sales, businessTypeFilter]);

  const byDay = (key) => enrichedSales.filter(s => s._key === key && (businessTypeFilter === 'All' || s._matchCount > 0));
  const sumBill = (list) => list.reduce((s, r) => s + r._revenue, 0);
  const sumProfit = (list) => list.reduce((s, r) => s + r._profit, 0);

  const todayList = byDay(todayKey);
  const yestList = byDay(yesterdayKey);

  const todaySales = sumBill(todayList);
  const yestSales = sumBill(yestList);
  const todayOrders = todayList.length;
  const yestOrders = yestList.length;
  const todayProfit = sumProfit(todayList);
  const yestProfit = sumProfit(yestList);

  const getReturnAmountForType = (r) => {
    if (businessTypeFilter === 'All') return Number(r.totalRefund || 0);
    return (r.items || []).filter(it => getBusinessType(it) === businessTypeFilter).reduce((s, it) => s + Number(it.returnTotal || 0), 0);
  };
  const returnsByKey = (key) => returns.filter(r => { const d = toDate(r.createdAt || r.date); return d && dateKey(d) === key; });
  const todayReturns = returnsByKey(todayKey).reduce((s, r) => s + getReturnAmountForType(r), 0);
  const yestReturns = returnsByKey(yesterdayKey).reduce((s, r) => s + getReturnAmountForType(r), 0);

  const pctChange = (curr, prev) => {
    if (prev === 0) return curr > 0 ? 100 : (curr === 0 ? null : 0);
    return Math.round(((curr - prev) / prev) * 100);
  };

  const lowStock = useMemo(() => products
    .filter(p => businessTypeFilter === 'All' || getBusinessType(p) === businessTypeFilter)
    .filter(p => Number(p.stock || 0) <= 5)
    .sort((a, b) => (a.stock || 0) - (b.stock || 0)), [products, businessTypeFilter]);

  const trend = useMemo(() => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const key = dateKey(d);
      const list = enrichedSales.filter(s => s._key === key);
      days.push({ label: d.toLocaleDateString('en-US', { weekday: 'short' }), sales: sumBill(list) });
    }
    return days;
  }, [enrichedSales]);
  const maxTrendVal = Math.max(1, ...trend.map(t => t.sales));

  const topProducts = useMemo(() => {
    const filteredStats = businessTypeFilter === 'All'
      ? topStats
      : topStats.filter(p => {
          const prod = products.find(pr => pr.id === p.id);
          return prod ? getBusinessType(prod) === businessTypeFilter : false;
        });
    return filteredStats.slice(0, 5).map(p => [p.name || 'Unknown', Number(p.qtySold || 0)]);
  }, [topStats, products, businessTypeFilter]);
  const maxTopQty = Math.max(1, ...topProducts.map(p => p[1]));

  const recentSales = useMemo(() => {
    return [...enrichedSales].filter(s => s._date).sort((a, b) => b._date - a._date).slice(0, 6);
  }, [enrichedSales]);

  const totalCustomers = customers.length;
  const newToday = customers.filter(c => { const d = toDate(c.createdAt); return d && dateKey(d) === todayKey; }).length;
  const phoneSaleCounts = useMemo(() => {
    const map = new Map();
    sales.forEach(s => { if (s.phone) map.set(s.phone, (map.get(s.phone) || 0) + 1); });
    return map;
  }, [sales]);
  const todayUniquePhones = Array.from(new Set(todayList.map(s => s.phone).filter(Boolean)));
  const repeatToday = todayUniquePhones.filter(p => (phoneSaleCounts.get(p) || 0) > 1).length;
  const repeatRate = todayUniquePhones.length > 0 ? Math.round((repeatToday / todayUniquePhones.length) * 100) : 0;
  const avgSpend = todayOrders > 0 ? Math.round(todaySales / todayOrders) : 0;

  const kpis = [
    { label: "Today's Sales", value: `৳ ${formatTk(todaySales)}`, icon: <span style={{ fontSize: '18px', fontWeight: 800, lineHeight: 1 }}>৳</span>, color: '#4a00e0', bg: '#f0f0ff', pct: pctChange(todaySales, yestSales), to: '/reports?tab=sales' },
    { label: "Today's Orders", value: todayOrders, icon: <ShoppingCart size={20} />, color: '#2e86de', bg: '#eaf4ff', pct: pctChange(todayOrders, yestOrders), to: '/reports?tab=sales' },
    { label: "Today's Profit", value: `৳ ${formatTk(todayProfit)}`, icon: <TrendingUp size={20} />, color: '#10b981', bg: '#eafff3', pct: pctChange(todayProfit, yestProfit), to: '/reports?tab=profit' },
    { label: "Today's Return", value: `৳ ${formatTk(todayReturns)}`, icon: <RotateCcw size={20} />, color: '#ef4444', bg: '#fff0f0', pct: pctChange(todayReturns, yestReturns), to: '/reports?tab=returns' },
  ];

  return (
    <div className="fade-in">
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {['All', ...BUSINESS_TYPES].map(t => (
          <button
            key={t}
            onClick={() => setBusinessTypeFilter(t)}
            style={{
              padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: '13px',
              background: businessTypeFilter === t ? '#4a00e0' : '#fff',
              color: businessTypeFilter === t ? '#fff' : '#666',
              boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
            }}
          >
            {t}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '20px' }}>
        {kpis.map((k, i) => (
          <Link key={i} to={k.to} className="white-box kpi-card" style={{ marginBottom: 0, display: 'block', textDecoration: 'none', color: 'inherit' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
              <span style={{ fontSize: '13px', color: '#888', fontWeight: 600 }}>{k.label}</span>
              <div style={{ width: '36px', height: '36px', borderRadius: '12px', background: k.bg, color: k.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {k.icon}
              </div>
            </div>
            <div style={{ fontSize: '24px', fontWeight: 800, color: '#1e293b', marginBottom: '6px' }}>{k.value}</div>
            <ChangeBadge pct={k.pct} />
          </Link>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="white-box" style={{ marginBottom: 0 }}>
            <h3 style={{ marginBottom: '20px' }}>📈 Sales Trend (Last 7 Days)</h3>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '14px', height: '180px' }}>
              {trend.map((t, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', height: '100%', justifyContent: 'flex-end' }}>
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'bold' }}>{t.sales > 0 ? formatTk(t.sales) : ''}</span>
                  <div style={{ width: '100%', maxWidth: '36px', height: `${Math.max(3, (t.sales / maxTrendVal) * 100)}%`, background: 'linear-gradient(180deg, #6a73fa, #4a00e0)', borderRadius: '8px 8px 3px 3px', transition: '0.3s' }}></div>
                  <span style={{ fontSize: '12px', color: '#888' }}>{t.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div className="white-box" style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ color: 'red', margin: 0 }}>⚠️ Low Stock</h3>
                <Link to="/stock" style={{ fontSize: '12px', color: '#4a00e0', fontWeight: 'bold', textDecoration: 'none' }}>View All →</Link>
              </div>
              {lowStock.length === 0 && <p style={{ color: '#999', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>All stock levels look healthy.</p>}
              {lowStock.slice(0, 5).map((p, i) => (
                <div key={i} style={{ padding: '10px 12px', background: '#fff5f5', borderRadius: '12px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', borderLeft: '4px solid red', fontSize: '13px' }}>
                  <span>{p.name}</span> <b style={{ color: 'red' }}>Qty: {p.stock}</b>
                </div>
              ))}
            </div>

            <div className="white-box" style={{ marginBottom: 0 }}>
              <h3 style={{ marginBottom: '15px' }}>📜 Recent Activity</h3>
              {recentSales.length === 0 && <p style={{ color: '#999', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No sales yet.</p>}
              {recentSales.map(s => (
                <div key={s.id} className="hover-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #f4f7fe' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>{s.name || 'Walk-In'}</div>
                    <div style={{ fontSize: '11px', color: '#999' }}>#{s.invoiceId} • {s._date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                  <b style={{ color: '#4a00e0', fontSize: '13px' }}>৳ {formatTk(s.totalBill)}</b>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="white-box" style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#f0f0ff', color: '#4a00e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Users size={18} />
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#888', fontWeight: 600 }}>Total Customers</div>
                <div style={{ fontSize: '20px', fontWeight: 800 }}>{totalCustomers}</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', textAlign: 'center', borderTop: '1px solid #f4f7fe', paddingTop: '15px' }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 800, color: '#4a00e0' }}>{repeatRate}%</div>
                <div style={{ fontSize: '10px', color: '#999' }}>Repeat Rate</div>
              </div>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 800, color: '#4a00e0' }}>{newToday}</div>
                <div style={{ fontSize: '10px', color: '#999' }}>New Today</div>
              </div>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 800, color: '#4a00e0' }}>৳ {formatTk(avgSpend)}</div>
                <div style={{ fontSize: '10px', color: '#999' }}>Avg Spend</div>
              </div>
            </div>
          </div>

          <div className="white-box" style={{ marginBottom: 0 }}>
            <h3 style={{ marginBottom: '15px' }}>🏆 Top Selling Products</h3>
            {topProducts.length === 0 && <p style={{ color: '#999', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No sales data yet.</p>}
            {topProducts.map(([name, qty], i) => (
              <div key={i} style={{ marginBottom: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '5px' }}>
                  <span>{name}</span><b>{qty} sold</b>
                </div>
                <div style={{ background: '#f0f0ff', borderRadius: '6px', height: '8px' }}>
                  <div style={{ width: `${(qty / maxTopQty) * 100}%`, background: 'linear-gradient(90deg, #6a73fa, #4a00e0)', height: '100%', borderRadius: '6px' }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
export default Home;
